import * as React from 'react';

import SelectDefault, { GroupBase, OptionsOrGroups, Props as SelectProps } from 'react-select';
import Async, { AsyncProps } from 'react-select/async';

import * as style from './Select.styl';

const WINDOW_COUNT = 100;

export interface IGenericOption<T = string> {
  label: string;
  value: T;
  [key: string]: any;
}

type SelectOrAsyncProps<TValue> = SelectProps<TValue, boolean, GroupBase<TValue>> | AsyncProps<TValue, boolean, GroupBase<TValue>>;

export class Select<TValue> extends React.Component<SelectOrAsyncProps<TValue>> {
  constructor(props: any) {
    super(props);
  }

  searchChildOptions(options: OptionsOrGroups<TValue, GroupBase<TValue>>, input: string) {
    const getLabelDelegate: (opt: any) => string = this.props.getOptionLabel || ((option) => option.label);
    const filteredChildOptions = [];

    for (const option of options) {
      const optionLabel = getLabelDelegate(option as any);

      if (filteredChildOptions.length < WINDOW_COUNT && optionLabel.toLowerCase().indexOf(input.toLowerCase()) >= 0) {
        filteredChildOptions.push(option);
      }
    }

    return filteredChildOptions;
  }

  // Note that this is only used if more than 100 options are passed in.
  asyncLoad(input: string) {
    const options = this.props.options as GroupBase<TValue>[];
    const value = this.props.value;
    const filteredOptions = [];
    const getValueDelegate: (opt: TValue) => any = this.props.getOptionValue || ((option) => option);
    const getLabelDelegate: (opt: any) => string = this.props.getOptionLabel || ((option) => option.label);
    for (const option of options) {
      const optionValue = getValueDelegate(option as any);
      const optionLabel = getLabelDelegate(option as any);

      if (value && optionValue === value) {
        // if the value of an option matches the currently selected value, add it to the current 'window' subset
        filteredOptions.push(option);
      } else if (option.options?.length && optionLabel.toLowerCase().indexOf(input.toLowerCase()) < 0) {
        // if the input doesn't match the option but the option has child options we need to search these child options
        // if the input matches child options we need to add the parent and all matched child options
        const matchedChildOptions = this.searchChildOptions(option.options, input);
        if (filteredOptions.length < WINDOW_COUNT && matchedChildOptions.length) {
          filteredOptions.push({ ...option, options: matchedChildOptions });
        }
      } else if (filteredOptions.length < WINDOW_COUNT && optionLabel.toLowerCase().indexOf(input.toLowerCase()) >= 0) {
        // if the input matches an option label and there are under a hundred in the current 'window' subset, add the option
        filteredOptions.push(option);
      }
    }
    return Promise.resolve(filteredOptions);
  }

  render() {
    let { options } = this.props;
    if (!options) {
      options = [];
    }
    if (options.length <= WINDOW_COUNT) {
      return (
        <SelectDefault<TValue, boolean, GroupBase<TValue>>
          {...(this.props as SelectProps<TValue, boolean, GroupBase<TValue>>)}
          styles={this.props.styles}
        />
      );
    }
    const asyncProps: any = { ...this.props };
    // we don't pass options directly to the AsyncSelect,
    // since they are loaded on `asyncLoad`
    delete asyncProps.options;

    return (
      <Async
        {...asyncProps}
        className={`${style.global} ${asyncProps.className ? asyncProps.className : ''}`}
        cacheOptions={false}
        defaultOptions
        loadOptions={(input) => this.asyncLoad(input)}
        styles={asyncProps.styles}
      />
    );
  }
}
